import { AUTHENTICATION_METHOD } from '../../domain/domainCodes.js'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Eye, EyeOff, GraduationCap, Lock, Mail, TriangleAlert, User } from 'lucide-react'
import AuthField from '../auth/AuthField'
import AuthHead from '../auth/AuthHead'
import AuthLayout from '../auth/AuthLayout'
import Button from '../../components/Button'
import Checkbox from '../../components/Checkbox'
import LoadingOverlay from '../../components/LoadingOverlay'
import ResultDialog from '../../components/ResultDialog'
import { transport } from '../../components/strings'
import { isEnglish } from '../../lib/i18n'
import { SESSION_STATUS, takeSessionEndedNotice, useSession } from '../../lib/sessionContext'
import { saveBranding } from '../../app/branding'
import { useResource } from '../../lib/useResource'
import {
  DEFAULT_LOGIN_CONFIG,
  SAVED_USERNAME_KEY,
  authenticate,
  getLoginConfig,
  goToLanding,
  requestPasswordReset,
  sendLoginLink,
  setPendingOtpUser,
  userManualHref,
} from './loginService'
import { strings as S } from './strings'

const LOGO_SRC = '/images/Rawam-logo-reporting.png'

export default function LoginPage() {
  const navigate = useNavigate()
  const { status } = useSession()

  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const [provider, setProvider] = useState('')

  const [fieldErrors, setFieldErrors] = useState({})
  /* WHY THIS SCREEN IS SHOWING, when it is showing because a session ended rather than
     because someone asked to sign in. Seeded once from the flag the redirect left behind —
     `takeSessionEndedNotice` clears it, so a refresh does not repeat it. Without this a
     session that lapsed mid-form dropped the user on a plain form with their work gone and
     nothing said. */
  const [error, setError] = useState(() => (takeSessionEndedNotice() ? transport.sessionEnded : ''))
  const [submitting, setSubmitting] = useState(false)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState(null)

  /* Which logo, which auth method, which optional links — GET /api/login, the JSON
     sibling of the page route this screen replaced. While it is in flight the form
     renders against the cautious defaults rather than flashing links on and off. */
  const { data } = useResource((signal) => getLoginConfig(signal), [])
  const cfg = data ?? DEFAULT_LOGIN_CONFIG
  const usesOnlyEntra = cfg.authMethod === AUTHENTICATION_METHOD.ENTRA_ID
  const usesPassLink = cfg.authMethod === AUTHENTICATION_METHOD.PASS_LINK
  const allowsEntra = cfg.authMethod === AUTHENTICATION_METHOD.ENTRA_ID || cfg.authMethod === AUTHENTICATION_METHOD.ENTRA_ID_AND_LOCAL
  const allowsPassword = !usesOnlyEntra && !usesPassLink
  const allowsLdap = cfg.authMethod === AUTHENTICATION_METHOD.ACTIVE_DIRECTORY || cfg.authMethod === AUTHENTICATION_METHOD.ACTIVE_DIRECTORY_AND_LOCAL

  /* The Razor page redirected an already-signed-in visitor to /Default. Client-side
     navigation never reaches that handler, so the check moves here. */
  useEffect(() => {
    if (status === SESSION_STATUS.authenticated) goToLanding()
  }, [status])

  useEffect(() => {
    if (cfg.brand) saveBranding(cfg.brand)
  }, [cfg.brand])

  // Legacy prefills the username saved by a previous "remember me" login.
  useEffect(() => {
    const saved = localStorage.getItem(SAVED_USERNAME_KEY)
    if (saved) {
      setUsername(saved)
      setRememberMe(true)
    }
  }, [])

  // The picker only appears once the config lands, so its default is chosen then.
  useEffect(() => {
    if (!provider && cfg.authMethod === AUTHENTICATION_METHOD.ACTIVE_DIRECTORY && cfg.ldapDataSources.length > 0) {
      setProvider(cfg.ldapDataSources[0].id)
    }
  }, [cfg.authMethod, cfg.ldapDataSources, provider])

  useEffect(() => {
    const externalError = new URLSearchParams(window.location.search).get('externalError')
    if (externalError) setError(S.messages.entraFailed)
  }, [])

  function validate() {
    const errors = {}
    if (usesPassLink && !/^\S+@\S+\.\S+$/.test(email.trim())) errors.email = S.messages.invalidEmail
    if (allowsPassword && username.trim() === '') errors.username = S.messages.required
    if (allowsPassword && password === '') errors.password = S.messages.required
    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    if (!validate()) return

    setSubmitting(true)
    if (usesPassLink) {
      const outcome = await sendLoginLink(email)
      setSubmitting(false)
      if (!outcome.status) {
        setError(outcome.message)
        return
      }
      setResult({ result: true, message: outcome.message || S.messages.loginLinkSent })
      return
    }

    const data = await authenticate({
      username,
      password,
      isPersistent: rememberMe,
      selectedAdProviderId: provider,
    })
    setSubmitting(false)

    if (!data.status) {
      setError(data.message)
      return
    }

    persistUsername()

    // The password check passed; the OTP step finishes the login.
    if (data.useOtp) {
      setPendingOtpUser(username, rememberMe)
      navigate('/login/otp')
      return
    }

    /* The cookie is set. Where this user belongs is the server's call, so hand the
       browser to /Default and let it decide — see goToLanding(). */
    goToLanding()
  }

  function persistUsername() {
    if (rememberMe) localStorage.setItem(SAVED_USERNAME_KEY, username)
    else localStorage.removeItem(SAVED_USERNAME_KEY)
  }

  async function handleForgotPassword() {
    if (username.trim() === '') {
      setFieldErrors({ username: S.messages.usernameRequired })
      return
    }

    setBusy(true)
    const data = await requestPasswordReset(username)
    setBusy(false)
    setResult({ result: data.Result, message: data.Message })
  }

  return (
    <AuthLayout>
      <form className="auth__card" onSubmit={handleSubmit} noValidate>
          <AuthHead
            title={S.title}
            subtitle={S.appTitle}
            mark={
              cfg.logoOnLoginPage && (
                <span
                  className={`auth__logo${cfg.logoBackgroundEnabled ? ' auth__logo--plate' : ''}`}
                >
                  <img src={LOGO_SRC} alt={S.logoAlt} />
                </span>
              )
            }
          />

          {usesPassLink && <AuthField
            id="txtEmail"
            label={S.email}
            icon={Mail}
            type="email"
            value={email}
            onChange={setEmail}
            error={fieldErrors.email}
            autoComplete="email"
          />}

          {allowsPassword && <AuthField
            id="txtUsername"
            label={S.username}
            icon={User}
            value={username}
            onChange={setUsername}
            error={fieldErrors.username}
            autoComplete="username"
          />}

          {allowsPassword && <AuthField
            id="txtPassword"
            label={S.password}
            icon={Lock}
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={setPassword}
            error={fieldErrors.password}
            autoComplete="current-password"
            trailing={
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={S.password}
              >
                {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            }
          />}

          {allowsLdap && cfg.ldapDataSources.length > 0 && (
            <fieldset className="auth__providers">
              <legend className="auth__providersTitle">{S.selectLogin}</legend>
              {cfg.authMethod === AUTHENTICATION_METHOD.ACTIVE_DIRECTORY_AND_LOCAL && (
                <label className="auth__provider">
                  <input
                    type="radio"
                    name="selectedAdProviderId"
                    value=""
                    checked={provider === ''}
                    onChange={() => setProvider('')}
                  />
                  <span>{S.localAccount}</span>
                </label>
              )}
              {cfg.ldapDataSources.map((source) => (
                <label key={source.id} className="auth__provider">
                  <input
                    type="radio"
                    name="selectedAdProviderId"
                    value={source.id}
                    checked={provider === source.id}
                    onChange={() => setProvider(source.id)}
                  />
                  <span>{(isEnglish && source.englishName) || source.arabicName}</span>
                </label>
              ))}
            </fieldset>
          )}

          {allowsEntra && cfg.entraDataSources.map((source) => (
            <Button
              key={source.id}
              type="button"
              variant="secondary"
              className="auth__submit"
              onClick={() => window.location.assign(`/Login/Entra/${source.id}?rememberMe=${rememberMe}`)}
            >
              {S.signInMicrosoft} — {(isEnglish && source.englishName) || source.arabicName}
            </Button>
          ))}

          {!usesPassLink && <div className="auth__row">
            <Checkbox id="chkBxRemindMe" checked={rememberMe} onChange={setRememberMe}>
              {S.rememberMe}
            </Checkbox>

            {allowsPassword && cfg.activateResetPasswordPage && (
              <button type="button" className="auth__link" onClick={handleForgotPassword}>
                <Lock size={14} aria-hidden="true" />
                {S.forgotPassword}
              </button>
            )}
          </div>}

          {allowsPassword && <Button type="submit" variant="primary" className="auth__submit" disabled={submitting}>
            {submitting ? S.submitting : S.title}
            {!submitting && <ArrowLeft className="ltr-flip" size={17} aria-hidden="true" />}
          </Button>}

          {usesPassLink && <Button type="submit" variant="primary" className="auth__submit" disabled={submitting}>
            {submitting ? S.submitting : S.sendLoginLink}
            {!submitting && <ArrowLeft className="ltr-flip" size={17} aria-hidden="true" />}
          </Button>}

          {error && (
            <p className="auth__error" role="alert">
              <TriangleAlert size={16} aria-hidden="true" />
              <bdi>{error}</bdi>
            </p>
          )}

          <footer className="auth__foot">
            {cfg.userGuideLinkOnTheLoginPage && (
              <a className="auth__link" href={userManualHref(cfg)} target="_blank" rel="noreferrer">
                <GraduationCap size={14} aria-hidden="true" />
                {S.userManual}
              </a>
            )}
            {cfg.showRegistrationLinkOnLoginPage && (
              <a className="auth__footLink" href="/signup">
                {S.newUser}
              </a>
            )}
          </footer>
        </form>

      <LoadingOverlay open={busy} label={S.waitScreen} />
      <ResultDialog result={result} closeLabel={S.close} onClose={() => setResult(null)} />
    </AuthLayout>
  )
}
